import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation } from 'react-router-dom'
import { Box, ThemeProvider, Tooltip, useMediaQuery, useTheme } from '@mui/material'

import { images } from '~assets/images'
import { Avatar, Icon, IconButton } from '~components/internal'
import { internalLightTheme } from '~createInternalTheme'
import { FEATURE_FLAGS } from '~featureFlags'
import { useFeatureGate } from '~hooks/access/useFeatureGate'
import { orgHandlesMatch, useUrlOrganizationHandle } from '~hooks/useUrlOrganizationHandle'
import { type RootState } from '~redux/rootReducer'
import { setSelectedEIOrganization } from '~redux/slices/organizationSlice'
import { accentColors, colors } from '~tokens/internal'

import './Sidebar.scss'

const EXPANDED_WIDTH = 260
const COLLAPSED_WIDTH = 80
const STORAGE_KEY = 'sidebar_v2_expanded'

type SidebarNavItemProps = {
  iconContent: React.ReactNode
  label: string
  ariaLabel?: string
  to?: string
  isActive: boolean
  isExpanded: boolean
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  trailingIcon?: React.ReactNode
  ref?: React.Ref<HTMLElement>
}

const SidebarNavItem = React.forwardRef<HTMLElement, SidebarNavItemProps>(
  ({ iconContent, label, ariaLabel, to, isActive, isExpanded, onClick, trailingIcon }, ref) => {
    const cls = `sidebar-v2__item${isActive ? ' sidebar-v2__item--active' : ''}`

    const content = (
      <>
        <span className='sidebar-v2__item-icon'>{iconContent}</span>
        {isExpanded && <span className='sidebar-v2__item-label'>{label}</span>}
        {isExpanded && trailingIcon != null && (
          <span className='sidebar-v2__item-chevron'>{trailingIcon}</span>
        )}
      </>
    )

    const inner = !isExpanded ? (
      <span className='sidebar-v2__item-tooltip-target'>{content}</span>
    ) : (
      content
    )

    const item = to ? (
      <NavLink
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={to}
        className={cls}
        aria-label={ariaLabel ?? (isExpanded ? undefined : label)}
      >
        {inner}
      </NavLink>
    ) : (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type='button'
        className={cls}
        onClick={onClick}
        aria-label={ariaLabel ?? (isExpanded ? undefined : label)}
      >
        {inner}
      </button>
    )

    return !isExpanded ? (
      <Tooltip title={label} placement='right'>
        {item}
      </Tooltip>
    ) : (
      item
    )
  },
)

SidebarNavItem.displayName = 'SidebarNavItem'

const SidebarContent: React.FC = () => {
  const muiTheme = useTheme()
  const isNarrowViewport = useMediaQuery(muiTheme.breakpoints.down('md'))

  const [isExpanded, setIsExpanded] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) !== 'false'
  })

  const dispatch = useDispatch()
  const location = useLocation()
  const sidebarRef = useRef<HTMLElement>(null)
  const [autoHideExpandedPath, setAutoHideExpandedPath] = useState<string | null>(null)
  const [previousPathname, setPreviousPathname] = useState(location.pathname)
  if (previousPathname !== location.pathname) {
    setPreviousPathname(location.pathname)
    setAutoHideExpandedPath(null)
  }
  const isAutoHide =
    location.pathname === '/assistant' || location.pathname.startsWith('/assistant/')
  const isAutoHideExpanded = autoHideExpandedPath === location.pathname

  // Force collapsed state on narrow viewports (e.g. high zoom levels)
  const effectiveExpanded = isNarrowViewport ? false : isAutoHide ? isAutoHideExpanded : isExpanded
  const urlOrgHandle = useUrlOrganizationHandle()

  const selectedOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )
  const emailIdentities = useSelector(
    (state: RootState) => state?.organization.emailIdentities || [],
  )

  const { enabled: licensesAndPermitsEnabled } = useFeatureGate(FEATURE_FLAGS.LICENSES_AND_PERMITS)
  const { enabled: publicRecordsStudioEnabled } = useFeatureGate(
    FEATURE_FLAGS.PUBLIC_RECORDS_STUDIO,
  )
  const { enabled: managerStaffViewEnabled } = useFeatureGate(
    FEATURE_FLAGS.ENABLE_MANAGER_STAFF_VIEW,
  )
  const homePath = managerStaffViewEnabled ? '/home' : '/cases/'

  const urlOrg = urlOrgHandle
    ? emailIdentities.find((ei) => orgHandlesMatch(ei.handle, urlOrgHandle))
    : undefined
  const effectiveHandle = urlOrg?.handle || selectedOrganization?.handle
  const { enabled: knowledgeBaseRedesignEnabled } = useFeatureGate(
    FEATURE_FLAGS.KNOWLEDGE_BASE_REDESIGN,
    { organizationHandle: effectiveHandle ?? undefined },
  )

  useEffect(() => {
    if (urlOrg && urlOrg.handle !== selectedOrganization?.handle) {
      dispatch(setSelectedEIOrganization(urlOrg))
    }
  }, [urlOrg, selectedOrganization?.handle, dispatch])

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      effectiveExpanded ? `${EXPANDED_WIDTH}px` : `${COLLAPSED_WIDTH}px`,
    )
  }, [effectiveExpanded])

  useEffect(() => {
    if (!isAutoHideExpanded) return
    const handlePointerDown = (event: MouseEvent): void => {
      if (!sidebarRef.current?.contains(event.target as Node)) setAutoHideExpandedPath(null)
    }
    document.addEventListener('mousedown', handlePointerDown)
    return (): void => document.removeEventListener('mousedown', handlePointerDown)
  }, [isAutoHideExpanded])

  const handleToggle = (): void => {
    if (isAutoHide) {
      setAutoHideExpandedPath(isAutoHideExpanded ? null : location.pathname)
      return
    }
    const next = !isExpanded
    setIsExpanded(next)
    localStorage.setItem(STORAGE_KEY, String(next))
  }

  const isPathActive = (path: string): boolean =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  return (
    <ThemeProvider theme={internalLightTheme}>
      <nav
        ref={sidebarRef}
        aria-label='Main'
        className={`sidebar-v2 do-not-print${effectiveExpanded ? ' sidebar-v2--expanded' : ' sidebar-v2--collapsed'}`}
      >
        <Box className='sidebar-v2__top'>
          <Box className='sidebar-v2__header'>
            {effectiveExpanded && (
              <NavLink to={homePath} aria-label='Go to home' className='sidebar-v2__logo-link'>
                <img src={images.altTextLogo} alt='Polimorphic' className='sidebar-v2__logo' />
              </NavLink>
            )}

            {!isNarrowViewport ? (
              <Tooltip
                title={effectiveExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
                placement='right'
                arrow
              >
                <IconButton
                  className={`sidebar-v2__toggle${effectiveExpanded ? ' sidebar-v2__toggle--flipped' : ''}`}
                  onClick={handleToggle}
                  aria-label={effectiveExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
                >
                  <Icon name='dockToRight' />
                </IconButton>
              </Tooltip>
            ) : (
              <NavLink to={homePath} aria-label='Go to home' className='sidebar-v2__logo-link'>
                <img src={images.logoWhite} alt='Polimorphic' className='sidebar-v2__logo' />
              </NavLink>
            )}
          </Box>

          {managerStaffViewEnabled && (
            <Box className='sidebar-v2__section sidebar-v2__section--unlabeled'>
              <Box className='sidebar-v2__items'>
                <SidebarNavItem
                  iconContent={
                    <Avatar
                      size={26}
                      iconSize={16}
                      backgroundColor={accentColors.orange.main}
                      iconName={<Icon name='workspaces' size={16} color={colors.neutral[0]} />}
                      ariaHidden
                      className='sidebar-v2__home-avatar'
                    />
                  }
                  label='Home'
                  to='/home'
                  isActive={isPathActive('/home')}
                  isExpanded={effectiveExpanded}
                />
              </Box>
            </Box>
          )}

          <Box className='sidebar-v2__section' role='group' aria-label='CRM & Workflows'>
            <span className='sidebar-v2__section-title'>
              {effectiveExpanded ? 'CRM & Workflows' : 'CRM'}
            </span>
            <Box className='sidebar-v2__items'>
              <SidebarNavItem
                iconContent={<Icon name='folder' size={18} />}
                label='Cases'
                to='/cases'
                isActive={isPathActive('/cases')}
                isExpanded={effectiveExpanded}
              />
              {licensesAndPermitsEnabled && (
                <SidebarNavItem
                  iconContent={<Icon name='license' size={18} />}
                  label='Licenses & Permits'
                  to='/licenses-and-permits'
                  isActive={isPathActive('/licenses-and-permits')}
                  isExpanded={effectiveExpanded}
                />
              )}
              <SidebarNavItem
                iconContent={<Icon name='group' size={18} />}
                label='Contacts'
                to='/contacts'
                isActive={isPathActive('/contacts')}
                isExpanded={effectiveExpanded}
              />
              <SidebarNavItem
                iconContent={<Icon name='process' size={18} />}
                label='Workflows'
                to='/processes'
                isActive={isPathActive('/processes')}
                isExpanded={effectiveExpanded}
              />
              <SidebarNavItem
                iconContent={<Icon name='payments' size={18} />}
                label='Payments'
                to='/bills'
                isActive={isPathActive('/bills')}
                isExpanded={effectiveExpanded}
              />
              <SidebarNavItem
                iconContent={<Icon name='barChart' size={18} />}
                label='Analytics'
                to='/analytics'
                isActive={isPathActive('/analytics')}
                isExpanded={effectiveExpanded}
              />
              {publicRecordsStudioEnabled && (
                <SidebarNavItem
                  iconContent={<Icon name='description' size={18} />}
                  label='Public Records'
                  to='/public-records'
                  isActive={isPathActive('/public-records')}
                  isExpanded={effectiveExpanded}
                />
              )}
            </Box>
          </Box>

          <Box className='sidebar-v2__section' role='group' aria-label='AI Front Desk'>
            <span className='sidebar-v2__section-title'>
              {effectiveExpanded ? 'AI Front Desk' : 'AI'}
            </span>
            <Box className='sidebar-v2__items'>
              <SidebarNavItem
                iconContent={<Icon name='callLog' size={18} />}
                label='Log'
                to={`/organizations/${effectiveHandle}/ai-log`}
                isActive={isPathActive(`/organizations/${effectiveHandle}/ai-log`)}
                isExpanded={effectiveExpanded}
              />
              <SidebarNavItem
                iconContent={<Icon name='barChart' size={18} />}
                label='Analytics'
                ariaLabel='AI Front Desk Analytics'
                to={`/organizations/${effectiveHandle}/analytics`}
                isActive={isPathActive(`/organizations/${effectiveHandle}/analytics`)}
                isExpanded={effectiveExpanded}
              />
              <SidebarNavItem
                iconContent={<Icon name='menuBook' size={18} />}
                label='Knowledge Base'
                to={`/organizations/${effectiveHandle}/sources`}
                isActive={
                  isPathActive(`/organizations/${effectiveHandle}/sources`) ||
                  (knowledgeBaseRedesignEnabled &&
                    isPathActive(`/organizations/${effectiveHandle}/health-check`))
                }
                isExpanded={effectiveExpanded}
              />
              {!knowledgeBaseRedesignEnabled && (
                <SidebarNavItem
                  iconContent={<Icon name='healthCheck' size={18} />}
                  label='Health Check'
                  to={`/organizations/${effectiveHandle}/health-check`}
                  isActive={isPathActive(`/organizations/${effectiveHandle}/health-check`)}
                  isExpanded={effectiveExpanded}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box className='sidebar-v2__bottom'>
          <SidebarNavItem
            iconContent={<Icon name='formatListBulleted' size={18} />}
            label='Menu'
            to='/menu'
            isActive={isPathActive('/menu')}
            isExpanded={effectiveExpanded}
          />
          <SidebarNavItem
            iconContent={<Icon name='settings' size={18} />}
            label='Settings'
            to='/settings-hub'
            isActive={isPathActive('/settings-hub')}
            isExpanded={effectiveExpanded}
          />
        </Box>
      </nav>
    </ThemeProvider>
  )
}

const Sidebar: React.FC = () => {
  return <SidebarContent />
}

export default Sidebar
